import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
} from "@mui/material";
import Icons from "../../Utils/Icons";
import * as XLSX from "xlsx";
import { POST } from "../Generic/CrudRequest";
import EncryptionUtils from "../../Utils/EncryptionUtils";
import fileToDownloadES from "../../Templates/ImportacionDeCargas.xlsx";
import fileToDownloadEN from "../../Templates/ImportLoad.xlsx";
import Alerts from "../../Utils/Alerts";
import { useTranslation } from "react-i18next";


export default function ImportLoad({ config }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Columnas requeridas para validar que estén presentes en el archivo Excel
  const requiredColumns = t("loads.import.requiredColumns", { returnObjects: true });
  
  // Campos obligatorios para la validación de datos
  const requiredFields = [
    "viaje",
    "vin", 
    "familia",
    "modelo",
    "centrodeDistribucion",
    "tractor"
  ];
  const [excelData, setExcelData] = useState([]);
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleFile = (file) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      const binaryString = event.target.result;
      setFileName(file.name);
      setFileSize(file.size);
      const workbook = XLSX.read(binaryString, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const firstRow = data[0];
      
      // Debug: Mostrar las columnas que llegan del Excel
      //console.log("Columnas del Excel:", firstRow);
      //console.log("Columnas requeridas:", requiredColumns);
      
      // Validar que todas las columnas requeridas estén presentes en el archivo Excel
      const missingColumns = requiredColumns.filter(
        (column) => !firstRow.some(
          (header) => header && header.toString().trim().toLowerCase() === column.trim().toLowerCase()
        )
      );

      if (missingColumns.length > 0) {
        setError(
          `${t("loads.import.requiredColumnsExcel")} ${missingColumns.join(
            ", "
          )}`
        );
        setExcelData([]);
      } else {
        setError("");

        const dataUser = EncryptionUtils.getDecryptedData("user");
        const parsedUser = JSON.parse(dataUser);

        const importLoadModels = data
          .slice(1)
          .map((rowData) => {
            const allColumnsEmpty = rowData.every(
              (value) => value === "" || value === undefined
            );

            if (allColumnsEmpty) {
              return null;
            }
            const modelData = {
              viaje: rowData[0] || "",
              vin: rowData[1] || "",
              familia: rowData[2] || "",
              idModelo: 0,
              modelo: rowData[3] || "",
              centrodeDistribucion: rowData[4] || "",
              idUbicacionOrigen: 0,
              bid: rowData[5] || "",
              distribuidor: rowData[6] || "",
              idUbicacionDestino: 0,
              destino: rowData[7] || "",
              tractor: rowData[8] || "",
              idMadrina: 0,
              bahia: rowData[9] || "",
              rampa: rowData[10] || "",
              status: true,
              orden: 0,
              idUsuario: parsedUser.userId,
              message: [],
            };

            // Validación de campos obligatorios para cada fila de datos
            const fieldValidations = [
              { field: 'viaje', message: t("loads.import.tripRequired") },
              { field: 'vin', message: t("loads.import.vinRequired") },
              { field: 'familia', message: t("loads.import.familyRequired") },
              { field: 'modelo', message: t("loads.import.modelRequired") },
              { field: 'centrodeDistribucion', message: t("loads.import.distributionCenterRequired") },
              { field: 'tractor', message: t("loads.import.madrinaRequired") }
            ];

            fieldValidations.forEach(({ field, message }) => {
              if (!modelData[field] || modelData[field] === "") {
                modelData.message.push(message);
              }
            });

            if (modelData.message.length > 0) {
              modelData.status = false;
            }

            return modelData;
          })
          .filter((model) => model !== null);

        const vinCounts = importLoadModels.reduce((acc, model) => {
          acc[model.vin] = (acc[model.vin] || 0) + 1;
          return acc;
        }, {});

        importLoadModels.forEach((model) => {
          if (vinCounts[model.vin] > 1) {
            model.message.push(t("loads.import.duplicateVin"));
            model.status = false;
          }
        });

        const validModels = importLoadModels.filter((model) => model.status);
        const responseLoads = await loadsError(validModels);

        responseLoads.forEach((responseModel) => {
          const matchingModel = importLoadModels.find(
            (model) => model.vin === responseModel.vin
          );
          if (matchingModel) {
            matchingModel.message.push(...responseModel.message);
            matchingModel.status = responseModel.status;
            matchingModel.idModelo = responseModel.idModelo;
            matchingModel.idUbicacionOrigen = responseModel.idUbicacionOrigen;
            matchingModel.idUbicacionDestino = responseModel.idUbicacionDestino;
            matchingModel.idMadrina = responseModel.idMadrina;
          }
        });

        importLoadModels.forEach((importLoadModel) => {
          if (importLoadModel.status === true) {
            importLoadModel.message.push(t("loads.import.canImport"));
          }
        });

        setExcelData(importLoadModels);
      }
    };

    reader.readAsBinaryString(file);
  };

  const loadsError = async (loads) => {
    const loadsError = await POST(
      `${config.url}/api/Carriers/Validar-cargas`,
      loads
    );
    return loadsError;
  };

  const saveImportLoad = async () => {
    if (excelData.length > 0) {
      const dataSend = excelData.filter((model) => model.status);

      if (dataSend.length > 0) {
        await POST(`${config.url}/api/Carriers/Agregar-cargas`, dataSend);
        navigate("/Cargas");
      } else {
        const message = t("loads.import.noValidLoads");
        const typeMessage = "warning";
        setAlertMessage(message);
        setAlertType(typeMessage);
        setShowAlert(true);
      }
    } else {
      const message = t("loads.import.noLoadsToImport");
      const typeMessage = "warning";
      setAlertMessage(message);
      setAlertType(typeMessage);
      setShowAlert(true);
    }
  };

  const handleClearData = () => {
    setExcelData([]);
    setFileName("");
    setFileSize(0);
    fileInputRef.current.value = null;
  };

  const handleSubmit = () => {
    saveImportLoad();
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    // Seleccionar el archivo según el idioma actual
    const currentLanguage = localStorage.getItem("language") || "es";
    const fileToDownload = currentLanguage === "en" ? fileToDownloadEN : fileToDownloadES;
    const fileName = currentLanguage === "en" ? "ImportLoad.xlsx" : "ImportacionDeCargas.xlsx";
    
    link.href = fileToDownload;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={handleCloseAlert}
        />
      )}
      <Card
        variant="outlined"
        sx={{
          overflow: "hidden",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <CardContent
            sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
          >
            <Icons
              iconName="ArrowBack"
              fontSize="large"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                marginRight: 20,
              }}
              to="/Cargas"
            />
            <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
              {t("loads.import.title")}
            </Typography>
          </CardContent>
          <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  margin: -2,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    flex: "1",
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("loads.import.importFile")}
                </Typography>
                <Icons
                  iconName="AssignmentReturned"
                  fontSize="large"
                  style={{
                    marginLeft: "auto",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.white.main,
                  }}
                  onClick={handleDownload}
                  tooltip={t("buttons.downloadTemplate")}
                />
                <Icons
                  iconName="Close"
                  fontSize="large"
                  onClick={handleClearData}
                  style={{
                    marginLeft: "15px",
                    backgroundColor: theme.palette.danger.main,
                    color: theme.palette.white.main,
                  }}
                  tooltip={t("buttons.clear")}
                />
              </CardContent>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {error && (
                      <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {error}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <div
                      onDrop={handleFileDrop}
                      onDragOver={handleDragOver}
                      style={{
                        textAlign: "center",
                        padding: "3px",
                        border: "2px dashed #aaa",
                        borderRadius: "4px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <input
                        type="file"
                        id="file_Documento"
                        multiple
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                        ref={fileInputRef}
                      />
                      {excelData.length !== 0 ? (
                        <Box>
                          <Icons
                            iconName="TextSnippet"
                            fontSize="large"
                            style={{
                              marginBottom: "1px",
                              color: theme.palette.primary.main,
                            }}
                          />
                          <Typography>
                            {fileName} - {fileSize} bytes
                          </Typography>
                        </Box>
                      ) : (
                        <label htmlFor="file_Documento">
                          <div>
                            <Icons
                              fontSize="60px"
                              iconName="CloudUpload"
                              style={{
                                marginBottom: "1px",
                                color: theme.palette.primary.main,
                              }}
                            />
                            <Typography>
                              {t("buttons.dragOrClick")}
                            </Typography>
                          </div>
                        </label>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <Typography
                variant="h5"
                sx={{
                  flex: "1",
                  color: theme.palette.primary.main,
                }}
              >
                {t("loads.import.loadsList")}
              </Typography>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <Paper
                sx={{
                  width: "100%",
                  margin: "auto",
                  overflow: "hidden",
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: "bold" }} key="carga">
                          {t("loads.import.table.trip")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="vin">
                          {t("loads.import.table.vin")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="familia">
                          {t("loads.import.table.family")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="modelo">
                          {t("loads.import.table.model")}
                        </TableCell>
                        <TableCell
                          style={{ fontWeight: "bold" }}
                          key="centrodeldistribuidor"
                        >
                          {t("loads.import.table.distributionCenter")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="bid">
                          {t("loads.import.table.bid")}
                        </TableCell>
                        <TableCell
                          style={{ fontWeight: "bold" }}
                          key="distribuidor"
                        >
                          {t("loads.import.table.distributor")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="destino">
                          {t("loads.import.table.distributorDestination")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="tractor">
                          {t("loads.import.table.madrina")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="bahia">
                          {t("loads.import.table.bay")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="rampa">
                          {t("loads.import.table.ramp")}
                        </TableCell>
                        <TableCell
                          style={{ fontWeight: "bold" }}
                          key="mensaje"
                          sx={{ width: "30%" }}
                        >
                          {t("loads.import.table.message")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!excelData || excelData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={13 + 1} align="center">
                            {t("loads.import.noLoadsAvailable")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        excelData.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            <TableCell>{row.viaje}</TableCell>
                            <TableCell>{row.vin}</TableCell>
                            <TableCell>{row.familia}</TableCell>
                            <TableCell>{row.modelo}</TableCell>
                            <TableCell>{row.centrodeDistribucion}</TableCell>
                            <TableCell>{row.bid}</TableCell>
                            <TableCell>{row.distribuidor}</TableCell>
                            <TableCell>{row.destino}</TableCell>
                            <TableCell>{row.tractor}</TableCell>
                            <TableCell>{row.bahia}</TableCell>
                            <TableCell>{row.rampa}</TableCell>
                            <TableCell sx={{ minWidth: "250px" }}>
                              {row.message.length > 0 ? (
                                <List
                                  sx={{ maxHeight: "150px", overflowY: "auto" }}
                                >
                                  {row.message.map((msg, index) => (
                                    <ListItem
                                      key={index}
                                      sx={{
                                        color:
                                          row.status === true
                                            ? theme.palette.primary.main
                                            : theme.palette.danger.main,
                                      }}
                                    >
                                      <ListItemText primary={msg} />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                ""
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  {t("buttons.save")}
                </Button>
              </Grid>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </>
  );
}
